---
title: The Window
lede: A window bar, the navigator on the left, the stage with its surfaces column on the right, the dock below, and a status bar.
order: 8
part: Find your way
---

The window is a fixed frame around the stage, where the open session's surfaces are shown. You arrange the stage; the frame stays put.

![The whole window](media/window-whole.webp)

### The window bar

- **Left**: the workspace name; click it to switch or create one. A **waiting** tag counts sessions that need you in other workspaces.
- **Middle**, on a session: its branches (source, `session/<id>`, and where it landed once merged), then the [target](/targets) it runs on if the workspace has any.
- **Right**: the update chip while an update is under way (see [Updates](/updates)), **Search**, on a session the **Workflow** button that opens its [run panel](/workflows), and **Agent**.

![The window bar](media/window-bar.webp)

**Search** (`⌘⇧P`) opens the command palette: sessions, projects, workspaces and commands in one list. **Agent** (`⌘J`) opens the [Agent panel](/agent-panel) at the window's right, where you ask an agent to change the page you are on.

### The navigator

The left column lists this workspace's sessions. **+** beside **Sessions** starts a task — see [Your first task](/first-task).

![The navigator](media/window-navigator.webp)

A row shows the session's name, how long since it last moved (or `error`), a square for its state, and its `session/<id>` branch. Drag rows to reorder them (`⌃1` … `⌃9` follow the order), double-click to rename, right-click for **Rename** and **Close session**.

At the foot are **Board**, **Archive**, **Workflows**, **Runs**, **Vault**, **Rules** (with a Team or Enterprise seat) and **Workspace settings**, then the button that narrows the column to icons (`⌘B`). Application settings are in the menu bar.

### Surfaces

The **Surfaces** column, on the right, lists the open session's surfaces as icons. Hover one to read its name, and click it to show it on the stage.

![The surfaces column, with the name of the hovered surface in its tip](media/surfaces-column.webp)

| Surface | Key | What it is |
|---|---|---|
| Claude Code or Codex | `⌘1` | The agent's terminal, with its state |
| Files | `⌘2` | The sandbox's files and editor |
| Changes | `⌘3` | The diff and the merge, with the number of changed files |
| Browser | `⌘4` | The pages the session serves |
| API console | `⌘5` | The project's saved calls |

On Windows and Linux these keys are not bound; use the column or the **View** menu.

**+** (**New agent**), under the surfaces, opens another agent beside the session's own. See [Side agents](/side-agents).

### Splitting the stage

Drag a surface from the column onto the stage: drop it in the middle of a surface to join that group, or on an edge to split the stage there.

![A split stage](media/surfaces-groups.webp)

Once split, the column shows one section per group, with a rule between them and a **×** at the head of each. Drag rows between sections; `⌥↑` and `⌥↓` move a row within its section. Drag the seam between groups to resize them. A section's **×** closes its group, and its surfaces join the group before it (the first group's join the second). The arrangement is the same for every session and survives a restart.

To move a surface to its own window, press **Open in a window** at the foot of the column, press `⌘⇧O`, or drag its row out of the window. **Bring it back** returns it.

### The dock

The dock, under the stage, holds **Projects & logs**, **Agent MCP** and one or more **VM shell** tabs; its **+** opens another shell. See [Projects and logs](/projects), [Agent MCP](/agent-preview) and [The VM shell](/vm-shell).

![The dock](media/window-dock.webp)

Open it from the three buttons at the left of the status bar, or toggle it with `⌥⌘J`. Drag its top edge to resize it, and its tabs to reorder them.

### The status bar

![The status bar](media/window-status.webp)

On a session, the bar shows the dock buttons (the first counts projects that are up), then, on the right, where the session runs: `vm · <id>` or `this computer · <id>`, with **Parked** if its machine is asleep. Hover `vm` for CPU, memory, disk, network and uptime; click `this computer` to reveal the session's folder. **Session** and **Weekly** show how much of your agent's limits you have used; click them to refresh.

It also shows queued sessions, a warm image being built, and trial days left. The speech bubble at its right end [sends a bug or an idea](/feedback). Away from a session, it names the workspace, its repositories and sessions, and how many are waiting.
